import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { DropdownContainer } from "../bits/inputs/DropdownContainer";
import { PrimaryButton, SecondaryButton } from "../bits/buttons";
import { css, useTheme } from "@emotion/react";
import { Box } from "@mui/material";
import Papa from "papaparse";
import { trpc } from "../../utils/trpc";

export type UploadBookAccessFormData = {
  files: File[];
};

type CsvType = {
  email: string;
  publicationId: string;
  name: string;
  organisationId?: string;
  remove?: string;
};

type UploadBookAccessFormProps = {
  onSubmit: (data: UploadBookAccessFormData) => void;
};

export const UploadBookAccessForm = ({
  onSubmit,
}: UploadBookAccessFormProps) => {
  const [formData, setFormData] = useState<UploadBookAccessFormData>({
    files: [],
  });
  const [error, setError] = useState<string>("");
  const theme = useTheme();

  const uploadBookAccess = trpc.uploadBookAccess.useMutation({
    onSuccess: () => {
      onSubmit(formData);
    },
    onError: (error) => {
      if (error.data?.zodError) {
        const fieldErrors = Object.entries(error.data.zodError.fieldErrors);
        if (fieldErrors) {
          const errorMessages = fieldErrors.map(
            ([key, value]) => `${key}: ${value}`,
          );
          return setError(`Error: ${errorMessages.join(", ")}`);
        } else if (error.data.zodError.formErrors.length) {
          return setError(
            `Error: ${error.data.zodError.formErrors.join(", ")}`,
          );
        }
      }
      setError("Error: " + error.message);
    },
  });

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (incomingFiles) => {
      setFormData((prev) => ({
        ...prev,
        files: incomingFiles,
      }));
    },
    accept: {
      "text/csv": [".csv"],
    },
    maxFiles: 1,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.files?.length) {
      const fileReader = new FileReader();
      fileReader.addEventListener(
        "load",
        () => {
          if (!fileReader.result) {
            setError("Error reading file");
            return;
          }
          if (typeof fileReader.result !== "string") {
            setError("Error reading file");
            return;
          }
          const parsed = Papa.parse<CsvType>(fileReader.result.trim(), {
            header: true,
          });

          if (parsed.errors.length) {
            console.error(parsed);
            setError("Error parsing file");
            return;
          }

          if (parsed.data.length === 0) {
            setError("No data found in file");
            return;
          }

          const data = parsed.data.map((row) => ({
            ...row,
            organisationId: row.organisationId
              ? parseInt(row.organisationId, 10)
              : undefined,
            remove: row.remove?.toLowerCase() === "true" ? true : false,
          }));

          uploadBookAccess.mutate({
            data,
          });
        },
        false,
      );
      fileReader.addEventListener(
        "error",
        () => {
          setError("Error reading file");
        },
        false,
      );
      fileReader.readAsText(formData.files[0]);
    }
  };

  const downloadExampleFile = () => {
    const element = document.createElement("a");
    const file = new Blob(
      [
        `publicationId,email,name,organisationId,remove
frp13e,example@example.com,,,
aag256,example2@example.com,example user,,
aag256,example3@example.com,,,true
aag256,example4@example.com,,1,true`,
      ],
      {
        type: "text/csv",
      },
    );
    element.href = URL.createObjectURL(file);
    element.download = "exampleFile.csv";
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ display: "flex", flexDirection: "column", gap: 2, paddingTop: 2 }}
    >
      {!formData.files?.length && (
        <DropdownContainer {...getRootProps()}>
          <input {...getInputProps()} />
          <p>Drag and drop or click here to upload the book access csv</p>
        </DropdownContainer>
      )}
      {!!formData.files?.length && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 8px;
          `}
        >
          <p>Selected file:</p>
          {formData.files?.map((image) => (
            <div
              key={image.name}
              css={css`
                display: flex;
                justify-content: space-between;
                width: 100%;
              `}
            >
              <span>{image.name}</span>
              <span>{Math.round(image.size / 1024)} KB</span>
              <PrimaryButton
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    files: [],
                  }));
                }}
              >
                Delete
              </PrimaryButton>
            </div>
          ))}
        </div>
      )}
      <SecondaryButton onClick={downloadExampleFile}>
        Download example file
      </SecondaryButton>
      <PrimaryButton type="submit">{"Upload Book Access"}</PrimaryButton>
      <p style={{ color: theme.palette.error.main }}>{error}</p>
    </Box>
  );
};
